import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Grow,
  Zoom,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CardActions,
  CardActionArea,
  Avatar,
  Badge
} from '@mui/material';
import {
  Work,
  LocationOn,
  BusinessCenter,
  People,
  School,
  EmojiEvents,
  Chat,
  Phone,
  Email,
  ArrowForward,
  ExpandMore,
  Security,
  Computer,
  Code,
  AccountBalance,
  TrendingUp,
  Assignment,
  DesignServices,
  Storage,
  Engineering,
  VerifiedUser,
  Download,
  Description,
  FileDownload,
  GetApp,
  PictureAsPdf,
  Article,
  Bookmark,
  Share,
  Star,
  StarBorder,
  AccessTime,
  MonetizationOn,
  CorporateFare,
  Groups,
  Diversity3,
  Handshake,
  Psychology,
  RocketLaunch,
  Diamond,
  Celebration,
  Spa,
  Nature,
  Park,
  LocalFlorist,
  Terrain,
  Opacity,
  Agriculture,
  AttachMoney,
  Timeline,
  Business,
  Store,
  Cloud,
  FlashOn,
  Language,
  Public,
  GpsFixed,
  DirectionsCar,
  LocalGasStation,
  Build,
  HomeWork,
  Apartment
} from '@mui/icons-material';

// Import images
import careersHero from '../images/x.jpg';
import teamImage from '../images/platinum.jpg';
import cultureImage from '../images/a.jpg';

const Careers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // PDF Documents mapping for each position
  const pdfDocuments = {
    "Commercial Bank Head of Credit": { 
      filename: "Head of Credit.pdf", 
      displayName: "Head of Credit Job Description"
    },
    "Head of Information Technology (IT)": { 
      filename: "Head of Information Technology (IT).pdf", 
      displayName: "Head of IT Job Description"
    },
    "Head of Operations (COO)": { 
      filename: "Head of Operations (COO).pdf", 
      displayName: "Head of Operations Job Description"
    },
    "Head of Treasury": { 
      filename: "Head of Treasury PDF.pdf", 
      displayName: "Head of Treasury Job Description"
    },
    "Payments and Reconciliations Officer": { 
      filename: "Payments and Reconciliations Officer.pdf", 
      displayName: "Payments Officer Job Description"
    },
    "Bank Teller": { 
      filename: "Bank Teller.pdf", 
      displayName: "Bank Teller Job Description"
    },
    "Chief Executive Officer (CEO)": { 
      filename: "Chief Executive Officer (CEO).pdf", 
      displayName: "CEO Job Description"
    },
    "Head Internal Audit": { 
      filename: "Head of Audit_Chartered Accoutant (CAE).pdf", 
      displayName: "Head of Internal Audit Job Description"
    }
  };

  // Helper function to get PDF info for a position
  const getPdfInfo = (positionTitle) => {
    return pdfDocuments[positionTitle] || null;
  };

  // Base path for documents - adjust this based on your server setup
const baseDocPath = "/docs/";

  // Function to handle download with proper filename
  const handleDownload = (pdfInfo) => {
    if (!pdfInfo) return;
    
    const link = document.createElement('a');
    link.href = `${baseDocPath}${encodeURIComponent(pdfInfo.filename)}`;
    link.download = pdfInfo.displayName || pdfInfo.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Career positions data - organized by department
  const careerPositions = [
    {
      department: "Wealth Management",
      icon: <AccountBalance />,
      color: "#4CAF50",
      positions: [
        {
          title: "Wealth Management Teller",
          requirements: [
            "10-years experience with Wealth Management Client Services",
            "Experience in CIT Cage, Safety Deposit Box Management, etc."
          ],
          salary: "Competitive Package",
          experience: "10+ years"
        }
      ]
    },
    {
      department: "Information Technology (IT) / FINTECH",
      icon: <Computer />,
      color: "#2196F3",
      positions: [
        {
          title: "IT Security Officer",
          requirements: [
            "Technology in Cyber Security Degree",
            "10 years Bank/IT Experience"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        },
        {
          title: "Head of Information Technology (IT)",
          requirements: [
            "Computer Science Degree",
            "10 years IT/Bank experience",
            "Leadership experience in banking IT"
          ],
          salary: "Executive Package",
          experience: "10+ years",
          featured: true
        },
        {
          title: "Systems Administrator",
          requirements: [
            "Computer Science Degree",
            "10 years IT/Bank experience"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        },
        {
          title: "Network Administrator",
          requirements: [
            "Computer Engineering degree",
            "Networking/Electronic Banking experience"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        }
      ]
    },
    {
      department: "TOP CODING EXPERTS - Mobile App & Internet Banking",
      icon: <Code />,
      color: "#9C27B0",
      positions: [
        {
          title: "Coding Expert (Java)",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Mobile Application Developer (Banking)",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "IOS Developer",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Web Developer",
          requirements: ["10 years Logo/Designing Experience"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Machine Learning Engineer (A.I.)",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Software Architect",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Software Developer",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Cyber Security Engineer",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Data Centre Dev Technician (DCT)",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Fibre Optic Installer/Technician",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        },
        {
          title: "Mobile Engineers/Designing",
          requirements: ["10 years Experience required"],
          salary: "Premium Package",
          experience: "10+ years"
        }
      ]
    },
    {
      department: "Executive Positions (10+ Years Experience)",
      icon: <TrendingUp />,
      color: "#FF9800",
      positions: [
        {
          title: "Compliance / AML Officer",
          requirements: [
            "BA Degree",
            "Compliance Management Certificate",
            "10 years AML Bank Experience"
          ],
          salary: "Executive Package",
          experience: "10+ years"
        },
        {
          title: "ACCOUNTS OFFICER",
          requirements: [
            "BCOMM Degree",
            "Articles (advantage)"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        },
        {
          title: "Commercial Bank Head of Credit",
          requirements: [
            "BCOMM Degree",
            "10-years in Credit"
          ],
          salary: "Executive Package",
          experience: "10+ years",
          featured: true
        },
        {
          title: "Head Internal Audit",
          requirements: [
            "CA designation required",
            "10 years in Bank Audit Department"
          ],
          salary: "Executive Package",
          experience: "10+ years",
          featured: true
        },
        {
          title: "SETTLEMENT OFFICER",
          requirements: [
            "BCOMM Degree",
            "ACI Settlement Certificate",
            "10 years Bank/Treasury Experience"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        },
        {
          title: "Head of Treasury",
          requirements: [
            "BCOMM, Finance & Investment Degree",
            "ACI Dealer Certificate",
            "10 years Treasury Experience"
          ],
          salary: "Executive Package",
          experience: "10+ years",
          featured: true
        },
        {
          title: "VP TREASURY",
          requirements: [
            "Treasury Window (International Experience)",
            "Director/Head of Trading Finance (Trading Desk)",
            "10 years International Experience"
          ],
          salary: "Executive Package",
          experience: "10+ years"
        },
        {
          title: "Trading Analyst",
          requirements: ["10 years Experience required"],
          salary: "Market Competitive",
          experience: "10+ years"
        },
        {
          title: "FORENSIC FINANCIAL ECONOMIST",
          requirements: [
            "Corporate Finance Degree in Economics",
            "Statistical Analysis domestic & international (10 years exp)",
            "International Markets RISK experience",
            "History of International Economics & Law (Equity, Law & Policy)",
            "Specializing in English Law",
            "10 years experience"
          ],
          salary: "Executive Package",
          experience: "10+ years"
        },
        {
          title: "Chief Executive Officer (CEO)",
          requirements: [
            "Executive leadership experience",
            "Strategic planning expertise",
            "10+ years banking experience"
          ],
          salary: "Executive Package",
          experience: "10+ years",
          featured: true
        },
        {
          title: "Payments and Reconciliations Officer",
          requirements: [
            "Accounting/Finance Degree",
            "10 years in Banking Operations",
            "Payment systems expertise"
          ],
          salary: "Market Competitive",
          experience: "10+ years",
          featured: true
        }
      ]
    },
    {
      department: "Corporate Services",
      icon: <DesignServices />,
      color: "#E91E63",
      positions: [
        {
          title: "Head of Operations (COO)",
          requirements: [
            "Operations Management Degree",
            "10 years+ Experience in Banking Operations",
            "Process improvement expertise"
          ],
          salary: "Executive Package",
          experience: "10+ years",
          featured: true
        },
        {
          title: "MARKETING & COMMUNICATIONS OFFICER",
          requirements: [
            "Marketing/Communications Degree",
            "10 years+ Experience in Marketing/Branding"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        },
        {
          title: "ADMINISTRATION OFFICER",
          requirements: [
            "Secretarial Certificate",
            "10 years Bank/Experience"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        },
        {
          title: "MERCHANT & CLIENT SERVICES OFFICER",
          requirements: [
            "Electronic & Digital Banking Experience"
          ],
          salary: "Market Competitive",
          experience: "10+ years"
        }
      ]
    },
    {
      department: "Teller Positions",
      icon: <AccountBalance />,
      color: "#00BCD4",
      positions: [
        {
          title: "Bank Teller",
          requirements: [
            "Customer service experience",
            "Cash handling proficiency",
            "Banking knowledge preferred"
          ],
          salary: "Competitive Package",
          experience: "Entry-level to 5 years",
          featured: true
        }
      ]
    },
    {
      department: "Board of Directors",
      icon: <VerifiedUser />,
      color: "#795548",
      positions: [
        {
          title: "NON-EXECUTIVE DIRECTOR (Banking Expert)",
          requirements: [
            "10 years Executive Banking Experience"
          ],
          salary: "Directorship Package",
          experience: "10+ years"
        }
      ]
    }
  ];

  // Legacy Project Goals with icons
  const legacyGoals = [
    { 
      goal: "100% of Staples grown locally by Q4 2026",
      icon: <Nature color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Production Funding to Mobilize SME Farmers",
      icon: <AttachMoney color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Two (2) million head of Cattle by Q4 2026",
      icon: <Business color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "One (1) million Sheep or Goats by Q4 2026",
      icon: <Park color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Carbon Neutral Eswatini (Fruit/Nut Trees & Lumber)",
      icon: <Nature color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Maximize Trade-Surplus/Currency Stabilization",
      icon: <Timeline color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Seven (7) years staples storage by Q4 2026",
      icon: <Storage color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Seven (7) years Petrol/Diesel Storage by Q4 2026",
      icon: <LocalGasStation color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Water Pipeline, Dam and Desalination Plant to irrigate rural & all arable land",
      icon: <Opacity color="primary" sx={{ fontSize: 30 }} />
    },
    { 
      goal: "Maximize employment in Agriculture sector",
      icon: <Agriculture color="primary" sx={{ fontSize: 30 }} />
    }
  ];

  // Application Requirements
  const applicationRequirements = [
    "Curriculum Vitae",
    "Government ID (Drivers License, Passport and/or National ID)",
    "10-year Experience in specific field (CA, CTO/IT, Banking)"
  ];

  const benefits = [
    {
      icon: <AttachMoney color="primary" sx={{ fontSize: 40 }} />,
      title: "Competitive Salary",
      description: "Attractive compensation packages with performance bonuses"
    },
    {
      icon: <School color="primary" sx={{ fontSize: 40 }} />,
      title: "Learning & Development",
      description: "Continuous professional development and training programs"
    },
    {
      icon: <EmojiEvents color="primary" sx={{ fontSize: 40 }} />,
      title: "Recognition & Rewards",
      description: "Performance-based rewards and recognition programs"
    },
    {
      icon: <Spa color="primary" sx={{ fontSize: 40 }} />,
      title: "Mental Wellness",
      description: "Employee assistance programs and wellness support"
    },
    {
      icon: <Groups color="primary" sx={{ fontSize: 40 }} />,
      title: "Team Culture",
      description: "Collaborative environment that values every member"
    },
    {
      icon: <RocketLaunch color="primary" sx={{ fontSize: 40 }} />,
      title: "Career Growth",
      description: "Clear progression pathways and promotion opportunities"
    }
  ];

  const contactMethods = [
    {
      icon: <Phone color="primary" sx={{ fontSize: 40 }} />,
      title: "Call Us",
      description: "+268 2505 0888",
      action: "Call Now",
      link: "tel:+26825050888"
    },
    {
      icon: <Email color="primary" sx={{ fontSize: 40 }} />,
      title: "Email Us",
      description: "careers@farmersbank.co.sz",
      action: "Send Email",
      link: "mailto:careers@farmersbank.co.sz"
    },
    {
      icon: <Chat color="primary" sx={{ fontSize: 40 }} />,
      title: "Live Chat",
      description: "Available Mon-Fri 8AM-5PM",
      action: "Start Chat",
      link: "#chat"
    }
  ];

  return (
    <Box sx={{ overflowX: 'hidden', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(19, 39, 20, 0.92), rgba(19, 39, 20, 0.92)), url(${careersHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: isMobile ? 'scroll' : 'fixed',
          color: 'white',
          py: 15,
          textAlign: 'center',
          mb: 8,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: `linear-gradient(to bottom, transparent, ${theme.palette.background.default})`,
            zIndex: 1
          }
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={800}>
            <Box>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                gutterBottom
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  textShadow: '0 4px 8px rgba(0,0,0,0.4)',
                  letterSpacing: '-0.5px'
                }}
              >
                Cultivate Your Career at <Box component="span" sx={{ color: theme.palette.secondary.main }}>Farmers Bank</Box>
              </Typography>
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 4, maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
                Join Eswatini's Premier Agricultural Financial Institution. We're seeking experienced professionals to drive innovation and growth.
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
                <Zoom in timeout={1000} style={{ transitionDelay: '200ms' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{
                      px: 6,
                      py: 1.5,
                      borderRadius: '12px',
                      boxShadow: '0 8px 25px rgba(255, 193, 7, 0.4)',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 30px rgba(255, 193, 7, 0.6)'
                      },
                      transition: 'all 0.3s ease',
                    }}
                    href="#positions"
                  >
                    Browse Open Positions
                  </Button>
                </Zoom>
                <Zoom in timeout={1000} style={{ transitionDelay: '400ms' }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    startIcon={<Download />}
                    sx={{
                      px: 6,
                      py: 1.5,
                      borderRadius: '12px',
                      borderWidth: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        borderWidth: 2,
                        transform: 'translateY(-3px)'
                      },
                      transition: 'all 0.3s ease',
                    }}
                    href="#apply"
                  >
                    Application Guide
                  </Button>
                </Zoom>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Stats Section */}
        <Box sx={{ mb: 10 }}>
          <Grid container spacing={3} justifyContent="center">
            {[
              { number: "50+", label: "Open Positions", icon: <Work /> },
              { number: "10+", label: "Years Experience Required", icon: <AccessTime /> },
              { number: "100%", label: "Growth Focused", icon: <TrendingUp /> },
              { number: "24/7", label: "Support System", icon: <Handshake /> }
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Grow in timeout={800 + index * 100}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.secondary.light}15)`,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                        background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      mb: 2
                    }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {stat.label}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Career Positions Section */}
        <Box id="positions" sx={{ mb: 12 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip 
                label="Join Our Team" 
                color="secondary" 
                sx={{ 
                  mb: 2, 
                  px: 2, 
                  py: 1, 
                  fontWeight: 700,
                  fontSize: '0.9rem'
                }} 
              />
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '6px',
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '3px'
                  }
                }}
              >
                Current Openings
              </Typography>
              <Typography variant="h5" sx={{ mt: 6, mb: 4, color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}>
                Explore specialized career opportunities across all departments. All positions require substantial expertise and experience.
              </Typography>
            </Box>
          </Fade>

          {careerPositions.map((department, deptIndex) => (
            <Box key={deptIndex} sx={{ mb: 8 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  mb: 4,
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${department.color}10, ${department.color}05)`,
                  borderLeft: `6px solid ${department.color}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 70,
                      height: 70,
                      bgcolor: department.color,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    {department.icon}
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        color: department.color,
                        fontWeight: 800,
                        fontSize: '2.2rem'
                      }}
                    >
                      {department.department}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {department.positions.length} Position{department.positions.length !== 1 ? 's' : ''} Available
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              
              <Grid container spacing={4}>
                {department.positions.map((position, posIndex) => {
                  const pdfInfo = getPdfInfo(position.title);
                  return (
                  <Grid item xs={12} md={6} lg={4} key={posIndex}>
                    <Grow in timeout={800 + (posIndex * 100)}>
                      <Card
                        elevation={position.featured ? 8 : 2}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: '16px',
                          borderTop: position.featured ? `6px solid ${theme.palette.secondary.main}` : `4px solid ${department.color}`,
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: position.featured 
                              ? '0 20px 40px rgba(255, 193, 7, 0.2)' 
                              : '0 15px 30px rgba(0,0,0,0.15)'
                          },
                          ...(position.featured && {
                            background: `linear-gradient(135deg, ${theme.palette.secondary.light}05, ${theme.palette.secondary.light}02)`
                          })
                        }}
                      >
                        {position.featured && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bgcolor: 'secondary.main',
                              color: 'white',
                              px: 2,
                              py: 0.5,
                              borderBottomLeftRadius: '8px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              zIndex: 2
                            }}
                          >
                            <Star sx={{ fontSize: 14, mr: 0.5 }} /> FEATURED
                          </Box>
                        )}
                        
                        <CardActionArea sx={{ flex: 1, p: 0 }}>
                          <CardContent sx={{ p: 3, pb: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.dark', flex: 1 }}>
                                {position.title}
                              </Typography>
                              {pdfInfo && (
                                <Tooltip title={`Download: ${pdfInfo.displayName}`}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(pdfInfo);
                                    }}
                                    sx={{
                                      ml: 1,
                                      bgcolor: 'primary.light',
                                      '&:hover': {
                                        bgcolor: 'primary.main',
                                        color: 'white'
                                      }
                                    }}
                                  >
                                    <GetApp />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                              <Chip 
                                icon={<AccessTime fontSize="small" />}
                                label={position.experience}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                              <Chip 
                                icon={<MonetizationOn fontSize="small" />}
                                label={position.salary}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontWeight: 600, fontSize: '0.9rem' }}>
                              Requirements:
                            </Typography>
                            <List dense disablePadding>
                              {position.requirements.map((req, reqIndex) => (
                                <ListItem key={reqIndex} disableGutters sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 30 }}>
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: department.color
                                      }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary={req}
                                    primaryTypographyProps={{ 
                                      variant: 'body2',
                                      sx: { fontSize: '0.9rem' }
                                    }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </CardContent>
                        </CardActionArea>
                        
                        <CardActions sx={{ p: 3, pt: 2, mt: 'auto' }}>
                          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              startIcon={<Description />}
                              href="mailto:Careers@FarmersBank.co.sz"
                              sx={{
                                borderRadius: '8px',
                                fontWeight: 600,
                                py: 1
                              }}
                            >
                              Apply Now
                            </Button>
                            {pdfInfo && (
                              <Tooltip title={`Download: ${pdfInfo.displayName}`}>
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<PictureAsPdf />}
                                  onClick={() => handleDownload(pdfInfo)}
                                  sx={{
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    minWidth: 'auto',
                                    px: 2
                                  }}
                                >
                                  PDF
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </CardActions>
                      </Card>
                    </Grow>
                  </Grid>
                )})}
              </Grid>
            </Box>
          ))}

          {/* Legacy Project Section */}
          <Paper
            elevation={3}
            sx={{
              p: 6,
              mt: 10,
              mb: 8,
              borderRadius: '24px',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-100px',
                right: '-100px',
                width: '300px',
                height: '300px',
                background: `radial-gradient(circle, ${theme.palette.secondary.main}30, transparent 70%)`,
                borderRadius: '50%'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-150px',
                left: '-150px',
                width: '400px',
                height: '400px',
                background: `radial-gradient(circle, ${theme.palette.secondary.main}20, transparent 70%)`,
                borderRadius: '50%'
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 6 }}>
                <Diamond sx={{ fontSize: 60, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                    The Legacy Project
                  </Typography>
                  <Typography variant="h5" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Farmers Bank's Vision for Eswatini's Future
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={4}>
                {legacyGoals.map((item, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Grow in timeout={800 + index * 100}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          height: '100%',
                          borderRadius: '12px',
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.12)',
                            transform: 'translateX(8px)'
                          }
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 60,
                          height: 60,
                          borderRadius: '12px',
                          bgcolor: 'secondary.main',
                          color: 'white'
                        }}>
                          {item.icon}
                        </Box>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                          {item.goal}
                        </Typography>
                      </Paper>
                    </Grow>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ 
                mt: 6, 
                pt: 4, 
                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'secondary.main' }}>
                  Target Completion: Q4 2026
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
                  Join us in building a sustainable future for Eswatini through agricultural innovation
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Application Requirements Section */}
        <Box id="apply" sx={{ mb: 12 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '6px',
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '3px'
                  }
                }}
              >
                Application Process
              </Typography>
              <Typography variant="h5" sx={{ mt: 6, mb: 4, color: 'text.secondary', maxWidth: '700px', mx: 'auto' }}>
                Complete your application with these essential documents and requirements
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={4} justifyContent="center">
            {applicationRequirements.map((requirement, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Grow in timeout={800 + (index * 200)}>
                  <Card
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: '16px',
                      borderLeft: `6px solid ${theme.palette.secondary.main}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 15px 30px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(87, 166, 73, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1) rotate(10deg)',
                          backgroundColor: 'rgba(87, 166, 73, 0.2)'
                        }
                      }}
                    >
                      <Assignment color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.dark', mb: 2 }}>
                      {requirement}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {index === 0 ? "Detailed professional experience and qualifications" :
                       index === 1 ? "Valid identification documents required" :
                       "Specific field expertise with verifiable experience"}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
          
          <Paper
            elevation={4}
            sx={{
              mt: 8,
              p: 6,
              borderRadius: '20px',
              background: `linear-gradient(135deg, ${theme.palette.secondary.light}08, ${theme.palette.primary.light}08)`,
              border: `2px solid ${theme.palette.secondary.light}20`,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 800 }}>
              Ready to Submit Your Application?
            </Typography>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
              📧 Email: <Box component="span" sx={{ color: 'primary.main' }}>Careers@FarmersBank.co.sz</Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}>
              Please send all required documents to the email above. Include the position title in your subject line.
              Only complete applications will be considered for review.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Email />}
              href="mailto:Careers@FarmersBank.co.sz"
              sx={{
                px: 6,
                py: 1.5,
                borderRadius: '12px',
                fontSize: '1.1rem',
                fontWeight: 700,
                boxShadow: '0 8px 25px rgba(87, 166, 73, 0.4)',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 30px rgba(87, 166, 73, 0.6)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Send Application
            </Button>
          </Paper>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ mb: 12 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '6px',
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '3px'
                  }
                }}
              >
                Why Choose Farmers Bank?
              </Typography>
              <Typography variant="h5" sx={{ mt: 6, mb: 4, color: 'text.secondary', maxWidth: '700px', mx: 'auto' }}>
                We invest in our team's growth and wellbeing with comprehensive benefits and a supportive environment
              </Typography>
            </Box>
          </Fade>
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Grow in timeout={800 + (index * 100)}>
                  <Card
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textAlign: 'center',
                      borderRadius: '16px',
                      borderTop: `4px solid ${theme.palette.secondary.main}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        '& .benefit-icon': {
                          transform: 'scale(1.2) rotate(10deg)',
                          bgcolor: 'rgba(87, 166, 73, 0.2)'
                        }
                      }
                    }}
                  >
                    <Box
                      className="benefit-icon"
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(87, 166, 73, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        mx: 'auto',
                        transition: 'all 0.4s ease'
                      }}
                    >
                      {benefit.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: 'primary.dark' }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1, fontSize: '1.05rem' }}>
                      {benefit.description}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Culture Section */}
        <Box
          sx={{
            color: 'white',
            borderRadius: '24px',
            p: { xs: 4, md: 8 },
            mb: 12,
            backgroundImage: `linear-gradient(rgba(19, 39, 20, 0.95), rgba(19, 39, 20, 0.95)), url(${cultureImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: isMobile ? 'scroll' : 'fixed',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-200px',
              right: '-200px',
              width: '500px',
              height: '500px',
              background: `radial-gradient(circle, ${theme.palette.secondary.main}20, transparent 70%)`,
              borderRadius: '50%',
              opacity: 0.5
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-250px',
              left: '-250px',
              width: '600px',
              height: '600px',
              background: `radial-gradient(circle, ${theme.palette.secondary.main}15, transparent 70%)`,
              borderRadius: '50%',
              opacity: 0.4
            }
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Typography
                  variant="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    textShadow: '0 4px 8px rgba(0,0,0,0.4)',
                    color: 'white'
                  }}
                >
                  Our <Box component="span" sx={{ color: theme.palette.secondary.main }}>Work Culture</Box>
                </Typography>
                <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto' }}>
                  A collaborative environment where innovation meets tradition, and every team member contributes to our agricultural legacy
                </Typography>
              </Box>
              
              <Grid container spacing={4} sx={{ mb: 6 }}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.12)'
                    }}
                  >
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3, color: 'secondary.main' }}>
                      Employee Experience
                    </Typography>
                    {[
                      "Flexible work arrangements & hybrid options",
                      "Continuous learning & development programs",
                      "Performance bonuses & incentive plans",
                      "Health, wellness & mental health support",
                      "Community volunteer opportunities",
                      "Employee recognition & awards programs"
                    ].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Celebration sx={{ mr: 2, color: 'secondary.main', fontSize: 20 }} />
                        <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.12)'
                    }}
                  >
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3, color: 'secondary.main' }}>
                      Growth & Innovation
                    </Typography>
                    {[
                      "Mentorship & leadership development",
                      "Team building & annual retreats",
                      "Innovation challenges & hackathons",
                      "Diversity & inclusion initiatives",
                      "Sustainable workplace practices",
                      "Work-life balance support programs"
                    ].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <RocketLaunch sx={{ mr: 2, color: 'secondary.main', fontSize: 20 }} />
                        <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              </Grid>
              
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 8,
                    py: 1.5,
                    borderRadius: '12px',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    boxShadow: '0 8px 25px rgba(255, 193, 7, 0.4)',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 30px rgba(255, 193, 7, 0.6)',
                      backgroundColor: 'secondary.dark'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  href="#contact"
                >
                  Explore Culture
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>

        {/* Contact Options Section */}
        <Box id="contact" sx={{ mb: 12 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '6px',
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '3px'
                  }
                }}
              >
                Get in Touch
              </Typography>
              <Typography variant="h5" sx={{ mt: 6, mb: 4, color: 'text.secondary', maxWidth: '700px', mx: 'auto' }}>
                Connect with our recruitment team through multiple channels. We're here to help you throughout the application process.
              </Typography>
            </Box>
          </Fade>
          <Grid container spacing={4} justifyContent="center">
            {contactMethods.map((method, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Grow in timeout={800 + (index * 200)}>
                  <Card
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: '16px',
                      transition: 'all 0.3s ease',
                      borderTop: `4px solid ${theme.palette.secondary.main}`,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        '& .contact-icon': {
                          transform: 'scale(1.1) rotate(-10deg)',
                          bgcolor: 'rgba(87, 166, 73, 0.2)'
                        }
                      }
                    }}
                  >
                    <Box
                      className="contact-icon"
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(87, 166, 73, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        transition: 'all 0.4s ease'
                      }}
                    >
                      {method.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: 'primary.dark' }}>
                      {method.title}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'secondary.main' }}>
                      {method.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                      {method.title === "Live Chat" 
                        ? "Our team is available to answer your questions during business hours"
                        : "Reach out to us directly for any inquiries about career opportunities"}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      endIcon={<ArrowForward />}
                      href={method.link}
                      sx={{
                        borderRadius: '8px',
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '1rem',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(87, 166, 73, 0.4)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {method.action}
                    </Button>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Final Application CTA */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Fade in timeout={800}>
            <Box>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '6px',
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '3px'
                  }
                }}
              >
                Ready to Cultivate Your Future?
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary', maxWidth: '700px', mx: 'auto' }}>
                Join Eswatini's leading agricultural financial institution and grow your career with purpose
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Zoom in timeout={800} style={{ transitionDelay: '200ms' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowForward />}
                    href="mailto:Careers@FarmersBank.co.sz"
                    sx={{
                      px: 8,
                      py: 2,
                      borderRadius: '12px',
                      fontSize: '1.2rem',
                      fontWeight: 800,
                      boxShadow: '0 8px 25px rgba(87, 166, 73, 0.4)',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 30px rgba(87, 166, 73, 0.6)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Apply Now
                  </Button>
                </Zoom>
                <Zoom in timeout={800} style={{ transitionDelay: '400ms' }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    startIcon={<Download />}
                    onClick={() => {
                      // Create and trigger download of application guide
                      const link = document.createElement('a');
                      link.href = `${baseDocPath}Application_Guide.pdf`;
                      link.download = 'Farmers_Bank_Application_Guide.pdf';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    sx={{
                      px: 8,
                      py: 2,
                      borderRadius: '12px',
                      fontSize: '1.2rem',
                      fontWeight: 800,
                      borderWidth: 2,
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        borderWidth: 2,
                        backgroundColor: 'rgba(255, 193, 7, 0.1)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Download Guide
                  </Button>
                </Zoom>
              </Box>
              <Typography variant="body1" sx={{ mt: 4, color: 'text.secondary', opacity: 0.8 }}>
                Applications reviewed on a rolling basis. Early submission recommended.
              </Typography>
            </Box>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
};

export default Careers;